import React from 'react';
import { AlertTriangle, CheckCircle, FileSearch, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const statusMeta = {
  ready_for_market_comparison: {
    label: 'Intern vergleichsbereit',
    icon: CheckCircle,
    className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  needs_document_extraction: {
    label: 'Primärbeleg fehlt',
    icon: FileSearch,
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  },
  needs_human_review: {
    label: 'Menschliche Prüfung',
    icon: AlertTriangle,
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  },
  not_comparable_by_default: {
    label: 'Nicht automatisch vergleichbar',
    icon: ShieldCheck,
    className: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300',
  },
};

const categoryLabels = {
  electricity: 'Strom', gas: 'Gas', district_heating: 'Fernwärme', water: 'Wasser',
  insurance: 'Versicherung', telecom: 'Internet/Telekom', mixed_utility: 'Sammelzahlung',
  maintenance: 'Wartung', other: 'Sonstiger Vertrag',
};

export default function CommercialReadinessPanel({ readiness, properties, selectedProperty }) {
  if (!readiness?.summary) return null;
  const propertyNames = new Map((properties || []).map((property) => [property.id, property.property_name || property.id]));
  const groups = (readiness.groups || []).filter((group) => selectedProperty === 'all' || group.propertyId === selectedProperty);
  const summary = readiness.summary;
  return (
    <Card className="border-indigo-200 dark:border-indigo-900/50 bg-gradient-to-br from-indigo-50/70 to-white dark:from-indigo-950/20 dark:to-background">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              Vertrags-Readiness
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Evidenzgebundene Vorbereitung vor einem möglichen Marktvergleich.
            </p>
          </div>
          <Badge variant="outline" className="border-indigo-300 text-indigo-700 dark:text-indigo-300">
            Keine Anbieteranfrage · kein Redirect
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-3">
            <p className="text-xs text-muted-foreground">Intern vergleichsbereit</p>
            <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{summary.readyForMarketComparison}</p>
          </div>
          <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 p-3">
            <p className="text-xs text-muted-foreground">Belegsuche</p>
            <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{summary.needsDocumentExtraction}</p>
          </div>
          <div className="rounded-lg bg-orange-50 dark:bg-orange-950/30 p-3">
            <p className="text-xs text-muted-foreground">Prüfung</p>
            <p className="text-xl font-bold text-orange-700 dark:text-orange-300">{summary.needsHumanReview}</p>
          </div>
          <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-3">
            <p className="text-xs text-muted-foreground">Nicht automatisch vergleichbar</p>
            <p className="text-xl font-bold">{summary.notComparableByDefault}</p>
          </div>
        </div>

        <div className="space-y-3">
          {groups.map((group) => (
            <div key={group.propertyId || 'unassigned'} className="rounded-lg border bg-background/70 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div>
                  <p className="font-semibold text-sm">{propertyNames.get(group.propertyId) || 'Objektzuordnung nicht belegt'}</p>
                  <p className="text-xs text-muted-foreground">
                    Eigentümer: {group.owner?.name || 'nicht belegt'} · {group.contracts.length} Vertrag{group.contracts.length === 1 ? '' : 'e'}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                {group.contracts.map((item) => {
                  const meta = statusMeta[item.classification] || statusMeta.needs_human_review;
                  const Icon = meta.icon;
                  return (
                    <div key={item.contractId} className="flex flex-col md:flex-row md:items-center gap-2 border-t pt-2 first:border-t-0 first:pt-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.contract.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {categoryLabels[item.contract.category] || item.contract.category} · Confidence {Math.round((item.readiness.confidence || 0) * 100)} %
                        </p>
                        {item.missingFields?.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Fehlt: {item.missingFields.map((field) => field.label).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="md:w-72 text-xs text-muted-foreground">{item.nextAction}</div>
                      <Badge className={`${meta.className} whitespace-nowrap flex items-center gap-1`}>
                        <Icon className="h-3.5 w-3.5" /> {meta.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
